import React from 'react';

const HEADING_FONT = 'Manrope, sans-serif';

/**
 * Logo Cloud / Partner Logos Block Renderer
 * Variants: grid, scroll, simple
 */
export default function LogoCloudRenderer({ props, variant }) {
  const {
    headline = '',
    logos = [],
    grayscale = false,
    background_color = '#FAFAF5',
  } = props;

  const filterClass = grayscale
    ? 'grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300'
    : '';

  const headerEl = headline ? (
    <div className="text-center mb-10">
      <h2
        className="text-lg font-semibold text-stone-500 uppercase tracking-wider"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
    </div>
  ) : null;

  if (variant === 'grid') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-items-center">
            {logos.map((logo, i) => (
              <LogoItem key={i} logo={logo} filterClass={filterClass} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'scroll') {
    // Duplicate logos for seamless infinite scroll
    const allLogos = [...logos, ...logos];
    return (
      <section className="py-12 px-0 overflow-hidden" style={{ backgroundColor: background_color }}>
        <div className="max-w-6xl mx-auto px-6">
          {headerEl}
        </div>
        <div className="relative">
          <div
            className="flex items-center gap-12 animate-marquee"
            style={{
              animation: 'marquee 30s linear infinite',
              width: 'max-content',
            }}
          >
            {allLogos.map((logo, i) => (
              <div key={i} className="flex-shrink-0">
                <LogoItem logo={logo} filterClass={filterClass} />
              </div>
            ))}
          </div>
          <style>{`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      </section>
    );
  }

  // Simple variant — single centered row
  return (
    <section className="py-12 px-6" style={{ backgroundColor: background_color }}>
      <div className="max-w-5xl mx-auto">
        {headerEl}
        <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
          {logos.map((logo, i) => (
            <LogoItem key={i} logo={logo} filterClass={filterClass} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LogoItem({ logo, filterClass }) {
  const img = (
    <img
      src={logo.image_url || 'https://placehold.co/160x60/e7e5e4/78716c?text=' + encodeURIComponent(logo.name || 'Logo')}
      alt={logo.name || 'Partner logo'}
      className={`h-10 sm:h-12 w-auto object-contain ${filterClass}`}
    />
  );

  if (logo.url) {
    return (
      <a href={logo.url} target="_blank" rel="noopener noreferrer" className="block">
        {img}
      </a>
    );
  }
  return img;
}
